// src/pages/StockInventory.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Boxes,
  Search,
  DollarSign,
  TrendingUp,
  Package,
  Layers,
  CheckCircle2,
  AlertTriangle,
  ArrowUpDown,
  Filter,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { StockOverviewResponse, StockItemDetail } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Link } from 'react-router-dom';

export const StockInventory: React.FC = () => {
  const { user } = useAuth();
  const { error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StockOverviewResponse | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState<'all' | 'in_stock' | 'out_of_stock' | 'deficit'>('all');
  const [valuationView, setValuationView] = useState<'base_cost' | 'net_cost'>('base_cost');

  const loadStockOverview = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('search', search);
      if (categoryFilter) query.append('category', categoryFilter);
      if (stockFilter !== 'all') query.append('stockFilter', stockFilter);

      const res = await apiRequest<StockOverviewResponse>(`/api/inventory/overview?${query.toString()}`);
      setData(res);
    } catch (err: any) {
      toastError(err.message || 'Failed to load stock overview.');
    } finally {
      setLoading(false);
    }
  }, [search, categoryFilter, stockFilter, toastError]);

  useEffect(() => {
    loadStockOverview();
  }, [loadStockOverview]);

  const summary = data?.summary || {
    total_skus: 0,
    in_stock_skus: 0,
    out_of_stock_skus: 0,
    deficit_skus: 0,
    total_units: 0,
    total_deficit_units: 0,
    total_base_cost_value: 0,
    total_net_cost_value: 0,
  };

  const categories = data?.categories || [];
  const items = data?.items || [];

  const formatCurrency = (val?: number) => {
    if (val === undefined || val === null) return 'AED 0.00';
    return `AED ${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6">
      {/* Title & Top Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">Stock Inventory & Valuation</h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1">
              <Boxes className="w-3 h-3 text-emerald-600" />
              Real-Time Stock Details
            </span>
          </div>
          <p className="text-xs text-slate-500">
            UAE Region (AED) • Monitor on-hand inventory balances, product level stock quantities, and valuation calculated on actual factory base cost
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle Base Cost vs Net Cost Valuation */}
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold">
            <button
              onClick={() => setValuationView('base_cost')}
              className={`px-3 py-1.5 rounded-md transition-all ${
                valuationView === 'base_cost'
                  ? 'bg-white text-slate-900 shadow-sm font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Actual Base Cost Value
            </button>
            <button
              onClick={() => setValuationView('net_cost')}
              className={`px-3 py-1.5 rounded-md transition-all ${
                valuationView === 'net_cost'
                  ? 'bg-white text-emerald-900 shadow-sm font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              Net Cost (Overhead Value)
            </button>
          </div>

          <Link to="/production-batches">
            <Button variant="outline" icon={<ExternalLink className="w-4 h-4 text-slate-600" />}>
              Ledger & Batches
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Stock Value */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {valuationView === 'base_cost' ? 'Total Stock Valuation (Base Cost)' : 'Total Stock Valuation (Net Cost)'}
            </span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700">
              <DollarSign className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">
              {formatCurrency(valuationView === 'base_cost' ? summary.total_base_cost_value : summary.total_net_cost_value)}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Calculated on actual physical quantity × {valuationView === 'base_cost' ? 'factory base unit cost' : 'net unit cost'}
            </p>
          </div>
        </div>

        {/* Total Units in Stock */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Total Units In Stock</span>
            <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700">
              <Boxes className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-slate-900 font-mono tracking-tight">
              {summary.total_units.toLocaleString()}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Aggregated across all pails, drums, rolls, sheets & kits
            </p>
          </div>
        </div>

        {/* In-Stock SKUs */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">In-Stock Catalog SKUs</span>
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-2xl font-black text-emerald-700 font-mono tracking-tight">
              {summary.in_stock_skus} <span className="text-sm font-semibold text-slate-400">/ {summary.total_skus}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {Math.round((summary.in_stock_skus / (summary.total_skus || 1)) * 100)}% product catalog availability
            </p>
          </div>
        </div>

        {/* Out of Stock & Deficit Alert */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Out of Stock & Deficit</span>
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-amber-700 font-mono tracking-tight">
                {summary.out_of_stock_skus}
              </span>
              {Boolean(summary.deficit_skus) && summary.deficit_skus > 0 && (
                <span className="text-[11px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">
                  {summary.deficit_skus} in deficit
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              {summary.deficit_skus > 0
                ? `${summary.deficit_skus} item(s) billed with negative balance pending production batch`
                : 'Items with 0 current inventory requiring production'}
            </p>
          </div>
        </div>
      </div>

      {/* Category Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {categories.map(c => (
          <div
            key={c.category}
            onClick={() => setCategoryFilter(categoryFilter === c.category ? '' : c.category)}
            className={`p-4 rounded-xl border transition-all cursor-pointer ${
              categoryFilter === c.category
                ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-emerald-500/30'
                : 'bg-white text-slate-800 border-slate-200 hover:border-slate-300 shadow-sm'
            }`}
          >
            <div className="flex items-center justify-between">
              <h4 className={`text-sm font-bold ${categoryFilter === c.category ? 'text-white' : 'text-slate-900'}`}>
                {c.category}
              </h4>
              <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                categoryFilter === c.category ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-600'
              }`}>
                {c.sku_count} SKUs
              </span>
            </div>

            <div className="mt-3 flex justify-between items-baseline">
              <div>
                <span className={`text-[10px] uppercase tracking-wider block ${categoryFilter === c.category ? 'text-slate-400' : 'text-slate-500'}`}>
                  Stock Balance
                </span>
                <span className="text-lg font-bold font-mono">
                  {c.units.toLocaleString()} units
                </span>
              </div>
              <div className="text-right">
                <span className={`text-[10px] uppercase tracking-wider block ${categoryFilter === c.category ? 'text-slate-400' : 'text-slate-500'}`}>
                  Valuation ({valuationView === 'base_cost' ? 'Base' : 'Net'})
                </span>
                <span className={`text-lg font-bold font-mono ${categoryFilter === c.category ? 'text-emerald-400' : 'text-emerald-700'}`}>
                  {formatCurrency(valuationView === 'base_cost' ? c.base_cost_val : c.net_cost_val)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
            <Input
              placeholder="Search product name or SKU..."
              className="pl-9 text-xs"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-[#0f5a2e]"
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.category} value={c.category}>
                {c.category} ({c.sku_count})
              </option>
            ))}
          </select>

          <select
            className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-[#0f5a2e]"
            value={stockFilter}
            onChange={e => setStockFilter(e.target.value as any)}
          >
            <option value="all">All Stock Statuses</option>
            <option value="in_stock">In Stock (&gt; 0)</option>
            <option value="out_of_stock">Out of Stock (0)</option>
            <option value="deficit">Deficit Stock (&lt; 0)</option>
          </select>

          <Button
            variant="outline"
            size="sm"
            onClick={loadStockOverview}
            icon={<RefreshCw className="w-3.5 h-3.5" />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Products Stock Inventory Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <TableSkeleton rows={10} columns={9} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Boxes className="w-10 h-10 text-slate-400" />}
            title="No inventory records found"
            description="No products matched your search or filters."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-center w-12 text-slate-400 font-mono">#</th>
                  <th className="px-4 py-3 text-left min-w-[280px]">Product / Material</th>
                  <th className="px-3 py-3 text-left w-32 whitespace-nowrap">Category</th>
                  <th className="px-3 py-3 text-center w-28 whitespace-nowrap">Status</th>
                  <th className="px-3 py-3 text-center w-20 whitespace-nowrap">Unit</th>
                  <th className="px-4 py-3 text-right text-slate-900 font-bold bg-slate-100/60 w-32 whitespace-nowrap">
                    Stock Balance
                    <span className="block text-[9px] text-slate-500 font-normal lowercase tracking-normal">units</span>
                  </th>
                  <th className="px-4 py-3 text-right bg-amber-50/40 text-amber-950 font-bold w-36 whitespace-nowrap">
                    Actual Base Cost (AED)
                    <span className="block text-[9px] text-amber-700/80 font-normal lowercase tracking-normal">unit factory cost</span>
                  </th>
                  <th className="px-4 py-3 text-right bg-emerald-50/40 text-emerald-950 font-bold w-40 whitespace-nowrap">
                    Total Valuation (AED)
                    <span className="block text-[9px] text-emerald-700/80 font-normal lowercase tracking-normal">qty × actual base cost</span>
                  </th>
                  <th className="px-4 py-3 text-right text-slate-600 w-36 whitespace-nowrap">
                    Net Valuation (AED)
                    <span className="block text-[9px] text-slate-400 font-normal lowercase tracking-normal">with overhead</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {items.map(it => (
                  <tr key={it.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-3 py-3 text-center font-mono text-xs text-slate-400">
                      {it.item_no}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900 text-sm leading-snug">{it.name}</div>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600 whitespace-nowrap">
                      {it.category}
                    </td>
                    <td className="px-3 py-3 text-center whitespace-nowrap">
                      {it.stock_balance > 0 ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                          In Stock
                        </span>
                      ) : it.stock_balance < 0 ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                          Negative Deficit
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                          Out of Stock
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center whitespace-nowrap font-mono text-xs text-slate-500">
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-slate-600 font-medium">
                        {it.unit || 'Unit'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-extrabold bg-slate-50/50 whitespace-nowrap">
                      <span className={it.stock_balance > 0 ? 'text-slate-900 font-bold' : it.stock_balance < 0 ? 'text-amber-800 font-black' : 'text-slate-400'}>
                        {it.stock_balance.toLocaleString()}
                        {it.stock_balance < 0 && (
                          <span className="text-[10px] block font-sans text-amber-700 font-bold">deficit</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-bold text-amber-900 bg-amber-50/20 whitespace-nowrap">
                      AED {it.base_unit_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-sm font-black text-emerald-900 bg-emerald-50/30 whitespace-nowrap">
                      AED {it.base_cost_valuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-600 whitespace-nowrap">
                      AED {it.net_cost_valuation.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
